import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, Col, Row, Button, Form, Alert, ProgressBar } from "react-bootstrap";
import { toast } from "react-hot-toast";
import axiosInstance from "helpers/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faPhone, faBuilding, faFileText, faShieldAlt } from "@fortawesome/free-solid-svg-icons";

const A2PSetup = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [businessInfo, setBusinessInfo] = useState({
    companyName: "",
    businessType: "llc",
    ein: "",
    businessAddress: "",
    authorizedRepName: "",
    authorizedRepTitle: "",
    phoneNumber: "",
    email: ""
  });

  const [setupComplete, setSetupComplete] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      navigate("/dashboard");
    }
  }, [sessionId, navigate]);

  const handleSubmitBusinessInfo = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post("/a2p/start-registration", {
        session_id: sessionId,
        business_info: businessInfo
      });

      if (response.data.success) {
        setCurrentStep(2);
        toast.success("Business information submitted successfully!");
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to submit business information");
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSetup = () => {
    setSetupComplete(true);
    setTimeout(() => {
      navigate("/dashboard");
    }, 3000);
  };

  const renderStep1 = () => (
    <div>
      <div className="text-center mb-4">
        <div 
          className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
          style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            boxShadow: '0 0 30px rgba(59, 130, 246, 0.3)'
          }}
        >
          <FontAwesomeIcon icon={faBuilding} className="text-white" size="2x" />
        </div>
        <h3 className="fw-bold" style={{ color: '#1f2937' }}>
          Business Registration Required
        </h3>
        <p className="text-muted">
          To activate your Virtual Office Number, we need to register your business for SMS compliance.
        </p>
      </div>

      <Form>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Company Name *</Form.Label>
              <Form.Control
                type="text"
                value={businessInfo.companyName}
                onChange={(e) => setBusinessInfo({...businessInfo, companyName: e.target.value})}
                required
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Business Type *</Form.Label>
              <Form.Select
                value={businessInfo.businessType}
                onChange={(e) => setBusinessInfo({...businessInfo, businessType: e.target.value})}
              >
                <option value="llc">LLC</option>
                <option value="corporation">Corporation</option>
                <option value="sole_proprietorship">Sole Proprietorship</option>
                <option value="partnership">Partnership</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>EIN (Tax ID) *</Form.Label>
              <Form.Control
                type="text"
                value={businessInfo.ein}
                onChange={(e) => setBusinessInfo({...businessInfo, ein: e.target.value})}
                placeholder="XX-XXXXXXX"
                required
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Business Phone *</Form.Label>
              <Form.Control
                type="tel"
                value={businessInfo.phoneNumber}
                onChange={(e) => setBusinessInfo({...businessInfo, phoneNumber: e.target.value})}
                placeholder="+1 (555) 123-4567"
                required
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>Business Address *</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={businessInfo.businessAddress}
            onChange={(e) => setBusinessInfo({...businessInfo, businessAddress: e.target.value})}
            placeholder="123 Main St, Suite 100, City, State 12345"
            required
          />
        </Form.Group>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Authorized Representative Name *</Form.Label>
              <Form.Control
                type="text"
                value={businessInfo.authorizedRepName}
                onChange={(e) => setBusinessInfo({...businessInfo, authorizedRepName: e.target.value})}
                required
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Title/Position *</Form.Label>
              <Form.Control
                type="text"
                value={businessInfo.authorizedRepTitle}
                onChange={(e) => setBusinessInfo({...businessInfo, authorizedRepTitle: e.target.value})}
                placeholder="Owner, CEO, etc."
                required
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-4">
          <Form.Label>Email Address *</Form.Label>
          <Form.Control
            type="email"
            value={businessInfo.email}
            onChange={(e) => setBusinessInfo({...businessInfo, email: e.target.value})}
            required
          />
        </Form.Group>
      </Form>

      <div className="d-flex justify-content-between">
        <Button
          variant="outline-secondary"
          onClick={() => navigate("/dashboard")}
        >
          Skip for Now
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmitBusinessInfo}
          disabled={loading || !businessInfo.companyName || !businessInfo.ein}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            border: 'none',
            borderRadius: '100px',
            padding: '12px 32px',
            fontWeight: 600
          }}
        >
          {loading ? (
            <>
              <div className="spinner-border spinner-border-sm me-2" />
              Submitting...
            </>
          ) : (
            "Submit Business Information"
          )}
        </Button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="text-center">
      <div 
        className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
        style={{
          width: '80px',
          height: '80px',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          boxShadow: '0 0 30px rgba(16, 185, 129, 0.3)'
        }}
      >
        <FontAwesomeIcon icon={faShieldAlt} className="text-white" size="2x" />
      </div>
      
      <h3 className="fw-bold mb-3" style={{ color: '#1f2937' }}>
        Registration In Progress
      </h3>
      
      <p className="text-muted mb-4">
        We're processing your business registration with our SMS compliance provider. 
        This typically takes 1-3 business days.
      </p>

      <Alert variant="info" className="mb-4">
        <FontAwesomeIcon icon={faPhone} className="me-2" />
        <strong>Your Virtual Office Number is ready!</strong><br />
        You can receive calls immediately. SMS functionality will be enabled once registration is complete.
      </Alert>

      <div className="mb-4">
        <h6>What happens next:</h6>
        <ul className="list-unstyled">
          <li className="mb-2">
            <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
            Business verification review (1-2 days)
          </li>
          <li className="mb-2">
            <FontAwesomeIcon icon={faFileText} className="text-warning me-2" />
            SMS compliance approval (1-2 days)
          </li>
          <li className="mb-2">
            <FontAwesomeIcon icon={faPhone} className="text-primary me-2" />
            Full SMS activation notification
          </li>
        </ul>
      </div>

      <Button
        variant="success"
        onClick={handleCompleteSetup}
        style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          border: 'none',
          borderRadius: '100px',
          padding: '12px 32px',
          fontWeight: 600
        }}
      >
        Continue to Dashboard
      </Button>
    </div>
  );

  const renderSuccess = () => (
    <div className="text-center">
      <div 
        className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
        style={{
          width: '100px',
          height: '100px',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          boxShadow: '0 0 40px rgba(16, 185, 129, 0.4)'
        }}
      >
        <FontAwesomeIcon icon={faCheckCircle} className="text-white" size="3x" />
      </div>
      
      <h2 className="fw-bold mb-3" style={{ color: '#1f2937' }}>
        Setup Complete!
      </h2>
      
      <p className="text-muted mb-4">
        Your Virtual Office Number has been activated and A2P registration is in progress.
        You'll receive email updates on your compliance status.
      </p>

      <div className="mb-4">
        <ProgressBar now={100} variant="success" style={{ height: '8px' }} />
        <small className="text-muted">Redirecting to dashboard...</small>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
      <Row className="h-100 g-0 justify-content-center align-items-center">
        <Col xs={12} md={8} lg={6} xl={5}>
          <Card 
            className="border-0 shadow-lg"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: '20px',
              overflow: 'hidden'
            }}
          >
            <Card.Header 
              className="border-0 text-center py-4"
              style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(29, 78, 216, 0.1) 100%)',
                borderRadius: '20px 20px 0 0'
              }}
            >
              <h4 className="fw-bold mb-0" style={{ color: '#1f2937' }}>
                Virtual Office Number Setup
              </h4>
              <p className="text-muted mb-0">
                Step {currentStep} of 2 - Business Registration
              </p>
            </Card.Header>

            <Card.Body className="p-5">
              {setupComplete ? renderSuccess() : currentStep === 1 ? renderStep1() : renderStep2()}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default A2PSetup;